import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from '@mui/material';
import { propertiesApi, getLandlordId, getUserEmail } from "../../services/api";

// Component imports
import PropertyHeader from "./components/PropertyHeader";
import PropertyGrid from "./components/PropertyGrid";
import { PropertyAddDialog, PropertyDeleteDialog } from "./components/PropertyDialog";

// Styles
import "./propertyList.css";
import {ApiDetails} from "../../Constants";

// Simple inline spinner component
const SimpleSpinner = () => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
            width: '100%'
        }}
    >
        <CircularProgress size={50} thickness={4} color="primary" />
        <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mt: 2, fontWeight: 500 }}
        >
            Loading properties...
        </Typography>
    </Box>
);

export default function PropertyList(props) {
    const navigate = useNavigate();

    // State management
    const [properties, setProperties] = useState([]);
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    // Dialog controls
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [addPropertyDialogOpen, setAddPropertyDialogOpen] = useState(false);
    const [propertyToDelete, setPropertyToDelete] = useState({ id: "", name: "" });

    // Get user details using utility functions
    const landlordId = getLandlordId();
    const emailAddress = getUserEmail();

    // Fetch properties on component mount
    useEffect(() => {
        props.refreshSideBar();
        fetchProperties();
    }, []);

    // Handle property navigation
    const navigateToProperty = (propertyId) => {
        navigate(`/property/${propertyId}`, {
            state: { property_id: propertyId }
        });
    };

    // Handle property deletion
    const handleDeleteProperty = (propertyId, propertyName) => {
        setPropertyToDelete({ id: propertyId, name: propertyName });
        setDeleteDialogOpen(true);
    };

    // Fetch properties from API
    const fetchProperties = async () => {
        setIsLoading(true);

        try {
            const response = await propertiesApi.getAll(landlordId);

            if (response.ok) {
                setProperties(response.data);
                setFilteredProperties(response.data);
            } else {
                props.snackBar({ text: "Failed Fetching Properties", type: "error" });
            }
        } catch (err) {
            console.error(err);
            props.snackBar({ text: "Connection Error", type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    // Delete property via API
    const confirmDeleteProperty = async () => {
        setIsLoading(true);

        try {
            const response = await propertiesApi.delete(propertyToDelete.id);

            if (response.ok) {
                props.snackBar({
                    text: "Property Deleted Successfully",
                    type: "success",
                });
                fetchProperties();
            } else {
                props.snackBar({
                    text: "Failed to delete property",
                    type: "error",
                });
            }
        } catch (err) {
            console.error(err);
            props.snackBar({
                text: "Something Went Wrong. Try Again Later",
                type: "error",
            });
            setDeleteDialogOpen(false);
            setIsLoading(false);
        }

    };

    // Handle property creation success
    const handlePropertyCreated = () => {
        fetchProperties();
        setAddPropertyDialogOpen(false);
    };

    // Handle search queries
    const handleSearch = (query) => {
        setSearchQuery(query);
        if (!query) {
            setFilteredProperties(properties);
            return;
        }

        const lowerQuery = query.toLowerCase();
        const filtered = properties.filter((property) =>
            property.property_name.toLowerCase().includes(lowerQuery) ||
            property.location.toLowerCase().includes(lowerQuery) ||
            property.total_property_units.toString().includes(lowerQuery) ||
            property.occupancy.toLowerCase().includes(lowerQuery)
        );

        setFilteredProperties(filtered);
    };

    // If loading, show the simple spinner
    if (isLoading && properties.length === 0) {
        return <SimpleSpinner />;
    }

    return (
        <div className="property-list-container">
            {/* Header with search and add button */}
            <PropertyHeader
                searchQuery={searchQuery}
                onSearchChange={handleSearch}
                onAddProperty={() => setAddPropertyDialogOpen(true)}
            />

            {/* Property Grid */}
            <PropertyGrid
                properties={filteredProperties}
                onViewProperty={navigateToProperty}
                onDeleteProperty={handleDeleteProperty}
                isLoading={isLoading}
            />

            {/* Delete Property Dialog */}
            <PropertyDeleteDialog
                open={deleteDialogOpen}
                propertyName={propertyToDelete.name}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDeleteProperty}
            />

            {/* Add Property Dialog */}
            <PropertyAddDialog
                open={addPropertyDialogOpen}
                onClose={() => setAddPropertyDialogOpen(false)}
                onPropertyCreated={handlePropertyCreated}
                landlordId={landlordId}
                emailAddress={emailAddress}
                apiDetails={ApiDetails}
                showSnackbar={props.snackBar}
            />
        </div>
    );
}